import { LiveStreamMessage, me, updateLiveSubscriptions, type TopicRefusal } from '@itc/shared';
import { api, ApiError } from '../api.ts';

// The browser's live connection (design §6.5, plan 1C Chunk 4), independent of Vue.
// One stream per page load, keyed by a tab id made here. Topics are reference-
// counted: the first listener subscribes, the last one leaving unsubscribes, and
// changes within 50 ms go to the server as one request. On `resync` (or a new
// server boot) every topic is subscribed again and every resource refetches.

/** The part of EventSource the client uses, so tests can supply their own. */
export interface EventSourceLike {
  readonly readyState: number;
  onopen: ((event: Event) => void) | null;
  onmessage: ((event: MessageEvent<string>) => void) | null;
  onerror: ((event: Event) => void) | null;
  close(): void;
}

export type EventSourceFactory = (url: string) => EventSourceLike;

export type LiveState = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'closed';

export interface WorkerStatus {
  configured: boolean;
  connected: boolean;
}

export interface TopicListener {
  onMessage: (data: unknown) => void;
  /** The server refused the topic, or took it away: nothing more arrives on it. */
  onRevoked?: (reason: string) => void;
  /** Messages may have been missed: fetch again. */
  onResync?: () => void;
}

export interface LiveClientOptions {
  eventSource?: EventSourceFactory;
  /** How long subscription changes are collected before one request. Default 50 ms. */
  batchMs?: number;
  /** Wall-clock milliseconds, for the server clock offset. */
  now?: () => number;
  /** How long to wait before making a new EventSource after the browser gave up on one. Default 2 s. */
  retryMs?: number;
  tab?: string;
}

const CLOSED = 2;

function newTabId(): string {
  const bytes = new Uint8Array(12);
  crypto.getRandomValues(bytes);
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export class LiveClient {
  readonly tab: string;
  readonly #factory: EventSourceFactory;
  readonly #batchMs: number;
  readonly #retryMs: number;
  readonly #now: () => number;
  readonly #topics = new Map<string, Set<TopicListener>>();
  readonly #pendingAdd = new Set<string>();
  readonly #pendingRemove = new Set<string>();
  readonly #stateListeners = new Set<(state: LiveState) => void>();
  readonly #workerListeners = new Set<(worker: WorkerStatus) => void>();
  readonly #notificationListeners = new Set<(message: Extract<LiveStreamMessage, { type: 'notification' }>) => void>();
  readonly #resyncListeners = new Set<() => void>();
  readonly #unauthorizedListeners = new Set<() => void>();
  /** Topics the server has answered for since they were last added: granted or not. */
  readonly #answered = new Map<string, boolean>();
  /** Topics the server holds for this tab, as far as this page has told it. */
  readonly #sent = new Set<string>();
  readonly #waiters = new Map<string, ((granted: boolean) => void)[]>();
  #source: EventSourceLike | undefined;
  #flushTimer: ReturnType<typeof setTimeout> | undefined;
  #retryTimer: ReturnType<typeof setTimeout> | undefined;
  #state: LiveState = 'idle';
  #started = false;
  #boot: string | undefined;
  #lastEventId: string | undefined;
  /** Set when this connection's hello already caused a resync, so the server's own `resync` doesn't repeat it. */
  #resyncedThisHello = false;
  #helloSeen = false;
  #offsetMs = 0;
  #worker: WorkerStatus = { configured: false, connected: false };

  constructor(options: LiveClientOptions = {}) {
    this.tab = options.tab ?? newTabId();
    this.#factory = options.eventSource ?? ((url) => new EventSource(url, { withCredentials: true }));
    this.#batchMs = options.batchMs ?? 50;
    this.#retryMs = options.retryMs ?? 2_000;
    this.#now = options.now ?? Date.now;
  }

  get state(): LiveState {
    return this.#state;
  }

  get worker(): WorkerStatus {
    return this.#worker;
  }

  /** The server's clock, from the offset measured on hello and pings. */
  serverNow(): Date {
    return new Date(this.#now() + this.#offsetMs);
  }

  /** The server's clock minus ours, in milliseconds. */
  get clockOffsetMs(): number {
    return this.#offsetMs;
  }

  /** Topics with at least one listener. */
  topics(): string[] {
    return [...this.#topics.keys()];
  }

  /** Opens the stream and sends every topic subscribed so far. Call after login. */
  start(): void {
    if (this.#started) return;
    this.#started = true;
    for (const topic of this.#topics.keys()) this.#queue(topic, 'add');
    this.#connect();
  }

  /** Closes the stream: on logout, or when the session is gone. Listeners stay, for the next start(). */
  stop(): void {
    this.#started = false;
    clearTimeout(this.#flushTimer);
    clearTimeout(this.#retryTimer);
    this.#flushTimer = undefined;
    this.#pendingAdd.clear();
    this.#pendingRemove.clear();
    this.#source?.close();
    this.#source = undefined;
    this.#boot = undefined;
    this.#lastEventId = undefined;
    this.#answered.clear();
    this.#sent.clear();
    this.#settleAll();
    this.#setState('closed');
  }

  /**
   * Resolves once the server has answered for `topic`, with whether it was granted:
   * from then on, every message on it reaches this page. Fetch a snapshot only after
   * this, or a message published between the fetch and the subscription is lost.
   * Resolves true at once when stopped, or when the server couldn't be asked.
   */
  whenSubscribed(topic: string): Promise<boolean> {
    const answered = this.#answered.get(topic);
    if (answered !== undefined) return Promise.resolve(answered);
    if (!this.#started || !this.#topics.has(topic)) return Promise.resolve(true);
    return new Promise((resolve) => {
      this.#waiters.set(topic, [...(this.#waiters.get(topic) ?? []), resolve]);
    });
  }

  #settle(topic: string, granted = true): void {
    for (const resolve of this.#waiters.get(topic) ?? []) resolve(granted);
    this.#waiters.delete(topic);
  }

  #settleAll(): void {
    for (const topic of [...this.#waiters.keys()]) this.#settle(topic);
  }

  /** Listens to a topic. Each call counts once, even with the same listener. Returns the function that stops listening. */
  subscribe(topic: string, listener: TopicListener): () => void {
    let listeners = this.#topics.get(topic);
    if (listeners === undefined) {
      listeners = new Set();
      this.#topics.set(topic, listeners);
      if (this.#started) this.#queue(topic, 'add');
    }
    const entry: TopicListener = { ...listener };
    listeners.add(entry);
    return () => {
      const current = this.#topics.get(topic);
      if (current === undefined || !current.delete(entry) || current.size > 0) return;
      this.#topics.delete(topic);
      this.#answered.delete(topic);
      this.#settle(topic);
      if (this.#started) this.#queue(topic, 'remove');
    };
  }

  onState(listener: (state: LiveState) => void): () => void {
    this.#stateListeners.add(listener);
    return () => this.#stateListeners.delete(listener);
  }

  onWorker(listener: (worker: WorkerStatus) => void): () => void {
    this.#workerListeners.add(listener);
    return () => this.#workerListeners.delete(listener);
  }

  onNotification(listener: (message: Extract<LiveStreamMessage, { type: 'notification' }>) => void): () => void {
    this.#notificationListeners.add(listener);
    return () => this.#notificationListeners.delete(listener);
  }

  /**
   * Called when the stream stopped because the session is gone (a 401): a ban, a
   * revoked session, lost access. The shell sends the page to login.
   */
  onUnauthorized(listener: () => void): () => void {
    this.#unauthorizedListeners.add(listener);
    return () => this.#unauthorizedListeners.delete(listener);
  }

  #unauthorized(): void {
    this.stop();
    for (const listener of this.#unauthorizedListeners) listener();
  }

  /** Called after every resync, once topics are subscribed again. */
  onResync(listener: () => void): () => void {
    this.#resyncListeners.add(listener);
    return () => this.#resyncListeners.delete(listener);
  }

  #setState(state: LiveState): void {
    if (state === this.#state) return;
    this.#state = state;
    for (const listener of this.#stateListeners) listener(state);
  }

  #connect(): void {
    if (!this.#started) return;
    this.#helloSeen = false;
    const last = this.#lastEventId === undefined ? '' : `&last=${encodeURIComponent(this.#lastEventId)}`;
    const source = this.#factory(`/api/live?tab=${encodeURIComponent(this.tab)}${last}`);
    this.#source = source;
    this.#setState(this.#boot === undefined ? 'connecting' : 'reconnecting');
    source.onmessage = (event) => {
      if (this.#source !== source) return;
      if (event.lastEventId !== '') this.#lastEventId = event.lastEventId;
      let message: LiveStreamMessage;
      try {
        message = LiveStreamMessage.parse(JSON.parse(event.data));
      } catch {
        return;
      }
      this.#receive(message);
    };
    source.onerror = () => {
      if (this.#source !== source) return;
      void this.#failed(source);
    };
  }

  /**
   * The stream failed. EventSource retries by itself, which would hide a 401 forever:
   * a stream that fails before hello checks /api/me, and stops when logged out.
   */
  async #failed(source: EventSourceLike): Promise<void> {
    if (this.#state === 'open') this.#setState('reconnecting');
    if (!this.#helloSeen) {
      try {
        await api(me, {}, { redirectOnUnauthorized: false });
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) {
          if (this.#source === source) this.#unauthorized();
          return;
        }
      }
    }
    if (this.#source !== source || !this.#started) return;
    if (source.readyState === CLOSED) {
      // The browser gave up (a non-200 answer): make a new one shortly.
      source.close();
      clearTimeout(this.#retryTimer);
      this.#retryTimer = setTimeout(() => {
        if (this.#source === source) this.#connect();
      }, this.#retryMs);
    }
  }

  #clock(serverTime: string): void {
    const server = Date.parse(serverTime);
    if (!Number.isNaN(server)) this.#offsetMs = server - this.#now();
  }

  #receive(message: LiveStreamMessage): void {
    switch (message.type) {
      case 'hello': {
        this.#helloSeen = true;
        this.#clock(message.serverTime);
        this.#setWorker(message.worker);
        const restarted = this.#boot !== undefined && this.#boot !== message.boot;
        this.#boot = message.boot;
        this.#resyncedThisHello = false;
        this.#setState('open');
        if (restarted) {
          this.#resyncedThisHello = true;
          void this.#resync();
        }
        return;
      }
      case 'message':
        for (const listener of [...(this.#topics.get(message.topic) ?? [])]) listener.onMessage(message.data);
        return;
      case 'notification':
        for (const listener of this.#notificationListeners) listener(message);
        return;
      case 'worker':
        this.#setWorker(message.worker);
        return;
      case 'revoked':
        this.#answered.set(message.topic, false);
        this.#sent.delete(message.topic);
        for (const listener of [...(this.#topics.get(message.topic) ?? [])]) listener.onRevoked?.(message.reason);
        return;
      case 'resync':
        if (this.#resyncedThisHello) {
          this.#resyncedThisHello = false;
          return;
        }
        void this.#resync();
        return;
      case 'ping':
        this.#clock(message.serverTime);
        return;
    }
  }

  #setWorker(worker: WorkerStatus): void {
    this.#worker = worker;
    for (const listener of this.#workerListeners) listener(worker);
  }

  /** Subscribes every topic again, then tells every listener to fetch again. */
  async #resync(): Promise<void> {
    this.#answered.clear();
    for (const topic of this.#topics.keys()) this.#queue(topic, 'add');
    await this.flush();
    for (const listeners of this.#topics.values()) for (const listener of [...listeners]) listener.onResync?.();
    for (const listener of this.#resyncListeners) listener();
  }

  #queue(topic: string, change: 'add' | 'remove'): void {
    const [into, out] = change === 'add' ? [this.#pendingAdd, this.#pendingRemove] : [this.#pendingRemove, this.#pendingAdd];
    out.delete(topic);
    into.add(topic);
    this.#flushTimer ??= setTimeout(() => {
      void this.flush();
    }, this.#batchMs);
  }

  /** Sends pending subscription changes now. Tests and resync call it; otherwise it runs 50 ms after a change. */
  async flush(): Promise<void> {
    clearTimeout(this.#flushTimer);
    this.#flushTimer = undefined;
    // A topic added and removed again within the batch needs nothing; the server never had it.
    const add = [...this.#pendingAdd].filter((topic) => this.#topics.has(topic));
    const remove = [...this.#pendingRemove].filter((topic) => !this.#topics.has(topic) && this.#sent.has(topic));
    this.#pendingAdd.clear();
    this.#pendingRemove.clear();
    if (!this.#started || (add.length === 0 && remove.length === 0)) return;
    let denied: TopicRefusal[];
    try {
      // At most 100 of each per request.
      denied = [];
      for (let start = 0; start < Math.max(add.length, remove.length); start += 100) {
        const answer = await api(updateLiveSubscriptions, { body: { tab: this.tab, add: add.slice(start, start + 100), remove: remove.slice(start, start + 100) } }, { redirectOnUnauthorized: false });
        denied.push(...answer.denied);
      }
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        this.#unauthorized();
        return;
      }
      // Couldn't subscribe (the server is down?): a waiting snapshot fetch goes ahead, and the reconnect resyncs.
      for (const topic of add) this.#settle(topic);
      return;
    }
    for (const topic of remove) this.#sent.delete(topic);
    const refused = new Map(denied.map(({ topic, reason }) => [topic, reason]));
    for (const topic of add) {
      const granted = !refused.has(topic);
      if (granted) this.#sent.add(topic);
      this.#answered.set(topic, granted);
      this.#settle(topic, granted);
    }
    for (const [topic, reason] of refused) for (const listener of [...(this.#topics.get(topic) ?? [])]) listener.onRevoked?.(reason);
  }
}
